/* eslint-disable import/no-cycle */

import jwtDecode from 'jwt-decode';
import Cookies from 'js-cookie';
import axios from 'axios';

import {
  httpErrorHandler,
  httpSuccessHandler,
  httpSetRequestHeaders,
} from '@ncl/modules/shared/services/httpMiddleware';
import { USER_ACCESS_TOKEN, USER_REFRESH_TOKEN } from '@constants/values';
import { sharedStore } from '@ncl/rootStore/rootStore.model';
import AccountStore from '../store/AccountStore';
import { logError } from '../../shared/utils';

class AccountServices {
  POLICYHOLDER_ORIGIN = 2;

  constructor() {
    this.instance = axios.create({
      baseURL: process.env.REACT_APP_BFF_SERVICE,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    this.instance.interceptors.response.use(httpSuccessHandler, (error) => {
      return httpErrorHandler(error, this.instance);
    });
    this.instance.interceptors.request.use(httpSetRequestHeaders);

    this.instance.defaults.raxConfig = {
      instance: this.instance,
    };
  }

  async registerPolicyholderUser(data) {
    return this.instance.post('/account/register/policyholder', data);
  }

  async registerAgentUser(data) {
    return this.instance.post('/account/register/agent', data);
  }

  async loginUser(login, password) {
    const data = {
      email: login,
      senha: password,
    };

    return this.instance.post('/account/login', data);
  }

  async logoutKeycloak() {
    const data = {
      refreshToken: this.getRefreshTokenCookie()
    };

    return this.instance.post('/account/logout', data);
  }

  async getUser() {
    return this.instance.get('/account/user');
  }

  getTermStatus(termResource, userId) {
    const url = `/terms/${termResource}`;
    const query = `id=${userId}${
      termResource === 'privacy' ? `&origin=${this.POLICYHOLDER_ORIGIN}` : ''
    }`;

    return this.instance.get(`${url}?${query}`);
  }

  async validatePendingTerms() {
    const isLoggedIn = this.isAuthenticated();
    if (!isLoggedIn) return;

    let userId;
    let role;

    if (process.env.REACT_APP_FEATURE_KEYCLOAK_AUTH === 'true') {
      const { currentUser } = AccountStore;
      ({ userId, role } = currentUser);
    } else {
      const decoded = this.getTokenDecodedValues();
      ({ userId, role } = decoded);
    }

    try {
      let termsResults;
      if (role === 'Agente') {
        termsResults = await Promise.all([
          this.getTermStatus('liability', userId),
          this.getTermStatus('privacy', userId),
          this.getTermStatus('resolution-382', userId),
        ]);
      } else {
        termsResults = await Promise.all([
          this.getTermStatus('liability', userId),
          this.getTermStatus('privacy', userId),
        ]);
      }
      const [{ data: liabilityTerms }, { data: privacyTerms }] = termsResults;
      if (!liabilityTerms.accepted) sharedStore.setCurrentLiabilityTerms(liabilityTerms);
      if (!privacyTerms.accepted) sharedStore.setCurrentPrivacyTerms(privacyTerms);

      if (role === 'Agente') {
        const { data: resolution382Terms } = termsResults[2];
        if (!resolution382Terms.accepted) {
          sharedStore.setCurrentResolution382Terms(resolution382Terms);
        }
      }
      const hasPending = termsResults.some((term) => !term.data.accepted);
      if (hasPending) {
        sharedStore.openPendingTermsModal();
      } else {
        const { data: judicialAppealNoRefundTerms } = await this.getTermStatus(
          'judicial-appeal-refund',
          userId
        );
        if (!judicialAppealNoRefundTerms.accepted) {
          sharedStore.setCurrentJudicialNoRefundTerms(judicialAppealNoRefundTerms);
        }
      }
    } catch (e) {
      logError('Erro ao recuperar status dos termos');
    }
  }

  acceptRequiredTerms(termId) {
    let userId;

    if (process.env.REACT_APP_FEATURE_KEYCLOAK_AUTH === 'true') {
      const { currentUser } = AccountStore;
      ({ userId } = currentUser);
    } else {
      const decoded = this.getTokenDecodedValues();
      ({ userId } = decoded);
    }

    return this.instance.post('/terms/accept', {
      termId,
      origin: this.POLICYHOLDER_ORIGIN,
      userId,
    });
  }

  async sendRecoverPasswordEmail(email) {
    const data = { email };

    return this.instance.post('/account/reset-password', data);
  }

  async redefinePassword(password, token) {
    const data = { password: encodeURIComponent(password), token };

    const url = '/account/reset-forgot-password';

    return this.instance.patch(`${url}`, data);
  }

  async sendEmailChange(email) {
    const data = { email: encodeURIComponent(email) };
    return this.instance.post('/account/change-email', data);
  }

  getTokenDecodedValues() {
    const token = this.getAccessTokenCookie();
    const decoded = token ? jwtDecode(token) : null;

    return decoded;
  }

  isAuthenticated() {
    if (process.env.REACT_APP_FEATURE_KEYCLOAK_AUTH === 'true') {
      const accessToken = this.getAccessTokenCookie();
      const refreshToken = this.getRefreshTokenCookie();
      const now = new Date();

      if (accessToken && refreshToken) {
        const decoded = jwtDecode(refreshToken);
        const expiry = decoded.exp * 1000;
        return now < expiry;
      }
      return false;
    }
    const decoded = this.getTokenDecodedValues();
    const now = new Date();

    if (decoded && now < decoded.exp * 1000) {
      return true;
    }
    return false;
  }

  isRegistered() {
    const isAuthenticated = this.isAuthenticated();
    const { hasUser } = AccountStore;

    return isAuthenticated || hasUser;
  }

  async setAccountStoreValues() {
    if (process.env.REACT_APP_FEATURE_KEYCLOAK_AUTH === 'true') {
      if (!this.getAccessTokenCookie() || !this.getTokenDecodedValues()) return;

      const { data } = await this.getUser();
      const { userId, federalId, name, companyName, role, email } = data;

      AccountStore.policyholder.cgcCnpj = federalId;
      AccountStore.policyholder.razaoSocial = companyName;
      AccountStore.currentUser = {
        userId,
        razaoSocial: companyName,
        displayName: name,
        federalId,
        email,
        role,
      };
    } else {
      if (!this.isAuthenticated()) return;

      const decoded = this.getTokenDecodedValues();
      AccountStore.policyholder.cgcCnpj = decoded.federalId;
      AccountStore.policyholder.razaoSocial = decoded.name;
      AccountStore.currentUser = {
        razaoSocial: decoded.name,
        displayName: decoded.name,
        federalId: decoded.federalId,
        email: decoded.email,
        role: decoded.role,
      };
    }
  }

  getRole() {
    if (process.env.REACT_APP_FEATURE_KEYCLOAK_AUTH === 'true') {
      const { currentUser } = AccountStore;
      return currentUser.role;
    }
    const decoded = this.getTokenDecodedValues();
    return decoded?.role;
  }

  setUserDataAfterLogin(userData) {
    const { cgcCnpj } = AccountStore.policyholder;
    if (process.env.REACT_APP_FEATURE_KEYCLOAK_AUTH === 'true') {
      const { userId, federalId, name, companyName, role, email } = userData;
      AccountStore.currentUser = {
        userId,
        displayName: name,
        federalId,
        razaoSocial: companyName,
        email,
        role,
      };
      if (!cgcCnpj) {
        AccountStore.policyholder.cgcCnpj = federalId;
        AccountStore.policyholder.razaoSocial = companyName;
      }
    } else {
      const decoded = this.getTokenDecodedValues();
      AccountStore.currentUser = {
        displayName: decoded.name,
        federalId: decoded.federalId,
        razaoSocial: decoded.name,
        email: decoded.email,
        role: decoded.role,
      };
      if (!cgcCnpj) {
        AccountStore.policyholder.cgcCnpj = decoded.federalId;
        AccountStore.policyholder.razaoSocial = decoded.name;
      }
    }
    this.validatePendingTerms();
  }

  async getCreditBalance() {
    const response = await this.instance.get('/credits/balance');
    return response;
  }

  async updateBalance() {
    const { setCreditsBalance } = sharedStore;

    try {
      const { data } = await this.getCreditBalance();
      setCreditsBalance({
        balance: data.balance,
        displayBalance: data.display,
      });
    } catch (error) {
      setCreditsBalance({
        balance: 0,
        displayBalance: false,
      });
    }
  }

  async validateToken(token) {
    const response = await this.instance.post('/account/validate-token', { token });
    return response;
  }

  async updateEmail(email, token) {
    const response = await this.instance.patch('/account/update-email', { newEmail: email, token });
    return response;
  }

  async updatePassword(currentPWD, newPWD) {
    const response = await this.instance.patch('/account/update-password', {
      new: newPWD,
      current: currentPWD,
    });

    return response;
  }

  setAccessTokenCookie(token, expires) {
    Cookies.set(USER_ACCESS_TOKEN, token, {
      domain: process.env.REACT_APP_DOMAIN,
      expires: new Date(new Date().getTime() + expires * 1000),
    });
  }

  setRefreshTokenCookie(refreshToken, expires) {
    Cookies.set(USER_REFRESH_TOKEN, refreshToken, {
      domain: process.env.REACT_APP_DOMAIN,
      expires: new Date(new Date().getTime() + expires * 1000),
    });
  }

  getAccessTokenCookie() {
    return Cookies.get(USER_ACCESS_TOKEN);
  }

  getRefreshTokenCookie() {
    return Cookies.get(USER_REFRESH_TOKEN);
  }

  removeAccessTokenCookie() {
    Cookies.remove(USER_ACCESS_TOKEN, {
      domain: process.env.REACT_APP_DOMAIN,
    });
  }

  removeRefreshTokenCookie() {
    Cookies.remove(USER_REFRESH_TOKEN, {
      domain: process.env.REACT_APP_DOMAIN,
    });
  }

  async getUserInfo(role) {
    let url = '/account/user';
    switch (role) {
      case 'Tomador':
        url = `${url}/policyholder`;
        break;
      case 'Consultor':
        url = `${url}/consultant`;
        break;
      case 'Agente':
        url = `${url}/agent`;
        break;
      default:
        break;
    }
    return this.instance.get(url);
  }

  async refreshToken() {
    const url = '/account/refresh';
    const refreshTokenCookie = this.getRefreshTokenCookie();
    if (!refreshTokenCookie) return false;
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };
    const data = `token=${refreshTokenCookie}`;
    return this.instance.post(url, data, { headers });
  }
}

const services = new AccountServices();
export default services;
